import { useEffect } from "react";

const SITE_NAME = "Dr. Erika Tajra";
const SITE_URL = "https://drerikatajra.com";
const DEFAULT_IMAGE = "https://drerikatajra.com/images/dr-erika-tajra.png";
const DEFAULT_DESCRIPTION = "Dr. Erika Tajra — UK-trained medical doctor specialising in nutrigenomics, microbiome science, and personalised longevity protocols. Book a consultation today.";

function setMeta(attr, key, content) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(Array.isArray(data) ? data : [data]);
}

export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  path = "",
  image = DEFAULT_IMAGE,
  type = "website",
  jsonLd,
  noindex = false,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Personalised Medicine & Nutrigenomics`;
  const canonical = `${SITE_URL}${path}`;

  useEffect(() => {
    document.title = fullTitle;

    setMeta("name", "description", description);
    setLink("canonical", canonical);

    if (noindex) {
      setMeta("name", "robots", "noindex,nofollow");
    } else {
      const robotsEl = document.querySelector('meta[name="robots"]');
      if (robotsEl) robotsEl.remove();
    }

    // Open Graph
    setMeta("property", "og:type", type);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:image", image);
    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:locale", "en_GB");

    // Twitter
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image);

    // JSON-LD
    if (jsonLd) {
      setJsonLd("seo-jsonld", jsonLd);
    }

    return () => {
      const el = document.getElementById("seo-jsonld");
      if (el) el.remove();
    };
  }, [fullTitle, description, canonical, image, type, noindex, jsonLd]);

  return null;
}

/* ── Pre-built JSON-LD generators ── */

export function personSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Dr. Erika Tajra",
    url: SITE_URL,
    image: DEFAULT_IMAGE,
    jobTitle: "Medical Doctor & Personalised Medicine Consultant",
    description:
      "UK-trained medical doctor specialising in nutrigenomics, microbiome science, and personalised longevity protocols.",
    alumniOf: [
      {
        "@type": "EducationalOrganization",
        name: "St George's University of London",
      },
      {
        "@type": "EducationalOrganization",
        name: "University College London (UCL)",
      },
    ],
    knowsAbout: [
      "Nutrigenomics",
      "Microbiome Science",
      "Personalised Medicine",
      "Longevity",
      "Fertility",
      "Genomics",
      "Epigenetics",
    ],
    sameAs: [],
  };
}

export function professionalServiceSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "Dr. Erika Tajra — Personalised Medicine",
    url: SITE_URL,
    image: DEFAULT_IMAGE,
    description: DEFAULT_DESCRIPTION,
    provider: personSchema(),
    areaServed: "Worldwide",
    serviceType: "Health & Wellness Consulting",
    address: {
      "@type": "PostalAddress",
      addressLocality: "London",
      addressCountry: "GB",
    },
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "17:00",
    },
  };
}

export function serviceSchema(service) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.title,
    description: service.description,
    provider: personSchema(),
    offers: {
      "@type": "Offer",
      price: service.price?.replace(/[^0-9.]/g, "") || "",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
    },
    url: `${SITE_URL}/services/${service.id}`,
  };
}

export function blogPostSchema(post) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    author: personSchema(),
    datePublished: post.created_at,
    dateModified: post.updated_at || post.created_at,
    url: `${SITE_URL}/blog/${post.slug}`,
    publisher: {
      "@type": "Organization",
      name: "Dr. Erika Tajra",
      url: SITE_URL,
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE_URL}/blog/${post.slug}`,
    },
  };
}

export function faqSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };
}
